import {compose} from 'redux';

import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {ConversationUpdater} from '../../../helpers/models/conversationHelpers';
import {safeAsyncDispatch} from '../../../helpers/reduxHelpers';
import {ActivityModel, isActivityPinned} from '../../../models/api/activityModel';
import {ConversationPatch} from '../../../models/api/conversationPatchModel';
import {ThunkAsyncAction} from '../../appThunk';
import {OptimisticUpdater} from '../../optimisticUpdates/optimisticUpdatesState';
import {updateConversationsAction} from '../conversations/updateConversationsActions';
import {DataState} from '../dataState';

interface PinAndUnpinActivitiesOptions {
  pinActivityIds?: ReadonlyArray<number>;
  unpinActivityIds?: ReadonlyArray<number>;
}

/*
 * Action Creators.
 */

export function pinActivityAction(activityId: number, conversationId: number): ThunkAsyncAction {
  return pinAndUnpinActivitiesAction(conversationId, {pinActivityIds: [activityId]});
}

export function unpinActivityAction(activityId: number, conversationId: number): ThunkAsyncAction {
  return pinAndUnpinActivitiesAction(conversationId, {unpinActivityIds: [activityId]});
}

export function pinAndUnpinActivitiesAction(
  conversationId: number,
  options: PinAndUnpinActivitiesOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const {userId} = getState().data;
    const pinActivityIds = options.pinActivityIds || [];
    const unpinActivityIds = options.unpinActivityIds || [];

    const deltaInNumberOfPinnedActivities = pinActivityIds.length - unpinActivityIds.length;

    await safeAsyncDispatch(
      dispatch(
        updateConversationsAction(
          [conversationId],
          buildPinAndUnpinActivitiesConversationPatch(pinActivityIds, unpinActivityIds),
          makeConversationUpdater(deltaInNumberOfPinnedActivities),
          {
            dataUpdater: makeActivitiesUpdater(pinActivityIds, unpinActivityIds, userId),
          },
        ),
      ),
    );
  };
}

/*
 * Helpers.
 */

// Optimistically mark the activities pinned/unpinned appropriately while waiting for the server.
const makeActivitiesUpdater = (
  activityIdsToPin: ReadonlyArray<number>,
  activityIdsToUnpin: ReadonlyArray<number>,
  pinnedByTeammateId: number,
): OptimisticUpdater =>
  compose(
    createActivitiesUpdater(activityIdsToPin, makeActivityPinnedReducer(pinnedByTeammateId)),
    createActivitiesUpdater(activityIdsToUnpin, activityUnpinnedReducer),
  );

// The conversation just tracks the numPinnedActivities, so that's all we need to update.
const makeConversationUpdater =
  (deltaInNumberOfPinnedActivities: number): ConversationUpdater =>
  (conversation) => {
    // If this is a ConversationBaseModel, nothing to update.
    if (!('numPinnedActivities' in conversation)) {
      return conversation;
    }

    return {
      ...conversation,
      numPinnedActivities: Math.max(0, conversation.numPinnedActivities + deltaInNumberOfPinnedActivities),
    };
  };

const createActivitiesUpdater = (
  activityIds: ReadonlyArray<number>,
  activityReducer: (activity: ActivityModel) => ActivityModel,
): OptimisticUpdater =>
  createObjectPropertyUpdater<DataState, 'activities'>(
    'activities',
    compose(...activityIds.map((id) => createResourceMapValueUpdater(id, createUpdater(activityReducer)))),
  );

const makeActivityPinnedReducer =
  (pinnedByTeammateId: number) =>
  (activity: ActivityModel): ActivityModel => {
    if (isActivityPinned(activity)) {
      return activity;
    }

    return {
      ...activity,
      pinnedByTeammateId,
      pinnedAt: Date.now(),
    };
  };

const activityUnpinnedReducer = (activity: ActivityModel): ActivityModel => {
  if (!isActivityPinned(activity)) {
    return activity;
  }

  return {
    ...activity,
    pinnedByTeammateId: undefined,
    pinnedAt: undefined,
  };
};

function buildPinAndUnpinActivitiesConversationPatch(
  pinActivityIds: ReadonlyArray<number>,
  unpinActivityIds: ReadonlyArray<number>,
): ConversationPatch {
  return {
    pinActivities: pinActivityIds,
    unpinActivities: unpinActivityIds,
  };
}
